<script lang="ts" setup>
import { ref, onMounted, computed, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useAxios } from '@/composables/useAxios'
import { handleAxiosError } from '@/composables/useAxiosError'
import { usePagination } from '@/composables'
import { STATUS_CODES } from '@/constants'
import { AdminK8sUsageOverviewItem, type PaginatedResponse } from '@/types'
import BaseDashboardPage from '@/components/base/BaseDashboardPage.vue'

defineOptions({ name: 'AdminK8sUsageOverview' })

const router = useRouter()

const loading = ref(true)
const items = ref<AdminK8sUsageOverviewItem[]>([])

const pagination = usePagination({ initialItemsPerPage: 50 })

async function fetchUsage() {
  loading.value = true
  const { limit, offset } = pagination.buildQueryParams()

  try {
    const res = await useAxios<null, PaginatedResponse<AdminK8sUsageOverviewItem>>(
      'GET',
      `/admin/k8s/usage/summary?limit=${limit}&offset=${offset}`,
      true
    )

    if (res && res.status === STATUS_CODES.OK && res.data) {
      const validate = AdminK8sUsageOverviewItem.array().safeParse(res.data.data)
      if (validate.success) {
        items.value = validate.data
      } else {
        items.value = res.data.data as AdminK8sUsageOverviewItem[]
      }
      pagination.setTotal(res.data.total)
    }
  } catch (err) {
    handleAxiosError(err as Error, { logPrefix: 'AdminK8sUsageOverview' })
  } finally {
    loading.value = false
  }
}

const showingFrom = computed(() => ((pagination.page.value - 1) * pagination.itemsPerPage.value) + 1)
const showingTo = computed(() => Math.min(showingFrom.value + items.value.length - 1, pagination.total.value))

function formatCurrency(cents: number): string {
  return (cents / 100).toFixed(2) + ' €'
}

function navigateToDetail(accountId: number) {
  router.push({ name: 'dashboardAdminK8sTenantDetail', params: { id: accountId } })
}

watch(() => pagination.page.value, () => {
  fetchUsage()
})

onMounted(() => {
  fetchUsage()
})
</script>

<template>
  <BaseDashboardPage
    :title="$t('dashboard.admin.kubernetes.usageOverview.title')"
    :subtitle="$t('dashboard.admin.kubernetes.usageOverview.subtitle')"
    icon="mdi-chart-bar"
    icon-color="cyan"
  >
    <template #actions>
      <button class="btn-text" @click="router.push({ name: 'dashboardAdminK8sHome' })">
        <v-icon icon="mdi-arrow-left" size="16" />
        {{ $t('dashboard.admin.kubernetes.title') }}
      </button>
    </template>

    <!-- Loading State -->
    <div v-if="loading" class="state-box">
      <v-progress-circular indeterminate size="32" width="3" color="#00B0FF" />
    </div>

    <!-- Empty State -->
    <div v-else-if="items.length === 0" class="state-box">
      <v-icon icon="mdi-chart-bar" size="48" class="empty-icon" />
      <p class="empty-title">{{ $t('dashboard.admin.kubernetes.usageOverview.empty.title') }}</p>
      <p class="empty-hint">{{ $t('dashboard.admin.kubernetes.usageOverview.empty.description') }}</p>
    </div>

    <!-- Usage Table -->
    <div v-else class="section-card">
      <div class="data-table">
        <div class="table-header usage-grid">
          <span class="col">{{ $t('dashboard.admin.kubernetes.usageOverview.table.namespace') }}</span>
          <span class="col">{{ $t('dashboard.admin.kubernetes.usageOverview.table.account') }}</span>
          <span class="col right">{{ $t('dashboard.admin.kubernetes.usageOverview.table.cpu') }}</span>
          <span class="col right">{{ $t('dashboard.admin.kubernetes.usageOverview.table.memory') }}</span>
          <span class="col right">{{ $t('dashboard.admin.kubernetes.usageOverview.table.storage') }}</span>
          <span class="col right">{{ $t('dashboard.admin.kubernetes.usageOverview.table.egress') }}</span>
          <span class="col right">{{ $t('dashboard.admin.kubernetes.usageOverview.table.projected') }}</span>
          <span class="col right">{{ $t('dashboard.admin.kubernetes.usageOverview.table.spendingCap') }}</span>
        </div>
        <div
          v-for="item in items"
          :key="item.account_id"
          class="table-row usage-grid clickable"
          @click="navigateToDetail(item.account_id)"
        >
          <span class="col mono">{{ item.namespace }}</span>
          <span class="col account-cell">
            <span class="account-email">{{ item.account_email }}</span>
            <span class="account-id">#{{ item.account_id }}</span>
          </span>
          <span class="col right mono">{{ item.cpu_core_hours.toFixed(1) }}</span>
          <span class="col right mono">{{ item.memory_gib_hours.toFixed(1) }}</span>
          <span class="col right mono">{{ item.storage_gib.toFixed(2) }}</span>
          <span class="col right mono">{{ item.egress_gib.toFixed(2) }}</span>
          <span class="col right mono">{{ formatCurrency(item.projected_total_cents) }}</span>
          <span class="col right mono">{{ item.spending_cap_cents ? formatCurrency(item.spending_cap_cents) : '-' }}</span>
        </div>
      </div>
    </div>

    <!-- Pagination -->
    <div v-if="pagination.total.value > 0 && !loading" class="pagination-controls">
      <div class="pagination-info">
        {{ $t('dashboard.admin.kubernetes.usageOverview.pagination.showing', { from: showingFrom, to: showingTo, total: pagination.total.value }) }}
      </div>
      <div class="pagination-buttons">
        <button class="pagination-btn" :disabled="pagination.page.value === 1" @click="pagination.goToPage(1)">
          <v-icon icon="mdi-chevron-double-left" size="18" />
        </button>
        <button class="pagination-btn" :disabled="pagination.page.value === 1" @click="pagination.prevPage()">
          <v-icon icon="mdi-chevron-left" size="18" />
        </button>
        <span class="page-indicator">{{ pagination.page.value }} / {{ pagination.totalPages.value }}</span>
        <button class="pagination-btn" :disabled="pagination.page.value === pagination.totalPages.value" @click="pagination.nextPage()">
          <v-icon icon="mdi-chevron-right" size="18" />
        </button>
        <button class="pagination-btn" :disabled="pagination.page.value === pagination.totalPages.value" @click="pagination.goToPage(pagination.totalPages.value)">
          <v-icon icon="mdi-chevron-double-right" size="18" />
        </button>
      </div>
    </div>
  </BaseDashboardPage>
</template>

<style lang="scss" scoped>
.btn-text {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: rgba(120, 163, 255, 0.08);
  border: 1px solid rgba(120, 163, 255, 0.15);
  border-radius: 8px;
  color: rgba(200, 218, 255, 0.8);
  font-size: 0.82rem;
  font-weight: 500;
  cursor: pointer;

  &:hover {
    background: rgba(120, 163, 255, 0.15);
  }
}

.section-card {
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(120, 163, 255, 0.08);
  border-radius: 16px;
  padding: 0;
  overflow: hidden;
}

.data-table {
  overflow-x: auto;
}

.usage-grid {
  display: grid;
  grid-template-columns: 1.5fr 1.5fr repeat(6, 1fr);
  gap: 12px;
  align-items: center;
  padding: 10px 16px;
  min-width: 900px;
}

.table-header {
  border-bottom: 1px solid rgba(120, 163, 255, 0.1);
  font-size: 0.75rem;
  font-weight: 600;
  color: rgba(200, 218, 255, 0.5);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.table-row {
  border-bottom: 1px solid rgba(120, 163, 255, 0.05);
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.85);
  transition: background 0.15s ease;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: rgba(120, 163, 255, 0.04);
  }

  &.clickable {
    cursor: pointer;
  }
}

.right {
  text-align: right;
}

.mono {
  font-family: 'JetBrains Mono', 'Fira Code', monospace;
  font-size: 0.85rem;
  font-weight: 500;
}

.account-cell {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.account-email {
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.8);
}

.account-id {
  font-size: 0.75rem;
  color: rgba(200, 218, 255, 0.5);
}

.state-box {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 48px 24px;
  text-align: center;
}

.empty-icon {
  color: rgba(200, 218, 255, 0.25);
  margin-bottom: 12px;
}

.empty-title {
  font-size: 1rem;
  font-weight: 500;
  color: rgba(200, 218, 255, 0.6);
  margin: 0 0 4px;
}

.empty-hint {
  font-size: 0.85rem;
  color: rgba(200, 218, 255, 0.4);
  margin: 0;
}

.pagination-controls {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  margin-top: 16px;
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(120, 163, 255, 0.08);
  border-radius: 12px;
}

.pagination-info {
  font-size: 0.85rem;
  color: rgba(200, 218, 255, 0.6);
}

.pagination-buttons {
  display: flex;
  align-items: center;
  gap: 8px;
}

.pagination-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(120, 163, 255, 0.15);
  border-radius: 8px;
  color: rgba(200, 218, 255, 0.7);
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: rgba(0, 176, 255, 0.1);
    border-color: rgba(0, 176, 255, 0.25);
    color: #00B0FF;
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
}

.page-indicator {
  font-size: 0.9rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.9);
  padding: 0 12px;
  min-width: 80px;
  text-align: center;
}

@media (max-width: 768px) {
  .pagination-controls {
    flex-direction: column;
    gap: 12px;
  }
}
</style>
